/**
 * URL Helpers for Multi-Subdomain Architecture
 *
 * CRM (staff): app.disputewell.com
 * Portal (clients): portal.disputewell.com or custom domain
 *
 * These helpers ensure proper URL construction for each domain,
 * preventing auth cookie conflicts between staff and client sessions.
 */

import { MINUTE_MS } from "@/lib/datetime/durations";
import { logger } from "@/lib/logging/logger";
import { routes } from "@/lib/routing/routes";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/tables/table-identifiers";
import { LRUCache } from "lru-cache";

/** Remove trailing slash from URL base */
function normalizeBase(url: string): string {
    return url.endsWith("/") ? url.slice(0, -1) : url;
}


/**
 * A path on the CRM (staff) origin: the origin of DW_CRM_LOGIN.
 */
export function getCrmUrl(path: string = ""): string {
    return `${new URL(process.env.DW_CRM_LOGIN).origin}${path}`;
}

/**
 * A path on the shared client portal origin: the origin of DW_PORTAL_LOGIN.
 */
export function getPortalUrl(path: string = ""): string {
    return `${new URL(process.env.DW_PORTAL_LOGIN).origin}${path}`;
}

/** True ONLY on the SHARED neutral portal host (creditportal.io in prod via
 *  DW_PORTAL_LOGIN; portal.lvh.me in dev) — a Scale/Enterprise org's
 *  CUSTOM portal domain (portal.acme.com) is NOT the shared host, even though
 *  it starts with "portal." (the trap in isPortalHostname's heuristic).
 *  Client-safe: next.config.ts inlines DW_PORTAL_LOGIN into the browser bundle.
 *  This is the render predicate for the platform attribution + the neutral
 *  pane on org-less auth surfaces (D-090). */
export function isSharedPortalHostname(hostname: string): boolean {
    const h = hostname.toLowerCase().replace(/:\d+$/, "");
    if (h === "portal.lvh.me" || h === "portal.localhost") {
        return true;
    }
    return h === new URL(process.env.DW_PORTAL_LOGIN).hostname;
}

/** True when a hostname is a dedicated portal host: the shared portal's own
 *  hostname (DW_PORTAL_LOGIN; creditportal.io in production, portal.lvh.me
 *  locally) when it is not the platform apex (DW_ADMIN_LOGIN's hostname), or an
 *  org's portal.<custom domain>. A portal that signs in on the apex itself shares
 *  that host, so the apex is not a portal host. Pure and host-only so it is safe to
 *  call client-side — e.g. from the shared /forgot-password and
 *  /reset-password auth pages, which render on BOTH the CRM and portal hosts. */
export function isPortalHostname(hostname: string): boolean {
    const h = hostname.toLowerCase();
    if (h === "portal.lvh.me" || h === "portal.localhost") {
        return true;
    }
    if (h.startsWith("portal.")) {
        return true;
    }
    const portalHostname = new URL(process.env.DW_PORTAL_LOGIN).hostname;
    return portalHostname !== new URL(process.env.DW_ADMIN_LOGIN).hostname && h === portalHostname;
}

function isDedicatedPortalHost(baseUrl: string): boolean {
    try {
        return isPortalHostname(new URL(baseUrl).hostname);
    } catch {
        return false;
    }
}

/** The login destination for the host a shared auth page (/forgot-password,
 *  /reset-password) is rendering on. Portal clients must return to the portal
 *  login (served at `/login` on the dedicated portal host), not the CRM login.
 *  The routes.crm.login literal mirrors routes.crm.login — kept literal here to
 *  avoid a routes → urls import cycle. */
export function authLoginHrefForHost(hostname: string): string {
    return isPortalHostname(hostname) ? "/login" : routes.crm.login;
}

// On a dedicated portal host (portal.example.com), paths like /portal/{slug}/{customerId}/dashboard
// are rewritten to /{slug}/{customerId}/dashboard. This matches the Next.js route structure where
// portal.example.com serves routes at the root rather than under /portal.
// On the shared app host, the /portal/{slug} prefix is kept for routing clarity.
function toShortPortalPath(path: string): string {
    if (!path) {
        return path;
    }
    if (path === "/portal") {
        return "/login";
    }
    return path.replace(/^\/portal\/([a-z0-9][a-z0-9-]*[a-z0-9]?)(?=\/|$)/i, "/$1");
}


function buildPortalImpersonationNextPath(orgSlug: string, customerId: string, nextPath?: string): string {
    const encodedCustomerId = encodeURIComponent(customerId);
    const dashboardRoot = `/portal/${orgSlug}/${encodedCustomerId}/dashboard`;
    const requested = nextPath?.trim();
    if (!requested) {
        return dashboardRoot;
    }

    const absoluteScopedRoot = `/portal/${orgSlug}/${encodedCustomerId}/dashboard`;
    if (requested === absoluteScopedRoot || requested.startsWith(`${absoluteScopedRoot}/`) || requested.startsWith(`${absoluteScopedRoot}?`)) {
        return requested;
    }

    const absoluteLegacyRoot = `/portal/${orgSlug}/dashboard`;
    if (requested === absoluteLegacyRoot || requested.startsWith(`${absoluteLegacyRoot}/`) || requested.startsWith(`${absoluteLegacyRoot}?`)) {
        return `${dashboardRoot}${requested.slice(absoluteLegacyRoot.length)}`;
    }

    const shortScopedRoot = `/${orgSlug}/${encodedCustomerId}/dashboard`;
    if (requested === shortScopedRoot || requested.startsWith(`${shortScopedRoot}/`) || requested.startsWith(`${shortScopedRoot}?`)) {
        return `/portal${requested}`;
    }

    const shortLegacyRoot = `/${orgSlug}/dashboard`;
    if (requested === shortLegacyRoot || requested.startsWith(`${shortLegacyRoot}/`) || requested.startsWith(`${shortLegacyRoot}?`)) {
        return `${dashboardRoot}${requested.slice(shortLegacyRoot.length)}`;
    }

    if (requested === "/dashboard" || requested.startsWith("/dashboard/") || requested.startsWith("/dashboard?")) {
        return `/portal/${orgSlug}/${encodedCustomerId}${requested}`;
    }

    return `${dashboardRoot}/${requested.replace(/^\/+/, "")}`;
}

/**
 * Build the redirect URL for portal staff impersonation
 * Routes through /auth/portal/impersonate which sets a secure cookie.
 *
 * GOTCHA: the `next` param must be the full portal path
 * (`/portal/{slug}/{customerId}/dashboard`) — NOT the short form
 * (`/{slug}/{customerId}/dashboard`).
 *
 * Reasoning:
 *  - On a shared host (a `DW_PORTAL_LOGIN` on the platform apex, e.g.
 *    local `localhost:3000`), the Next.js route only exists at
 *    `src/app/portal/[slug]/dashboard/(overview)/page.tsx`. Sending the short form
 *    `next=/{slug}/dashboard` 404s — that was the bug.
 *  - On a dedicated portal host (`portal.disputewell.com`, `portal.lvh.me`),
 *    the proxy in `src/proxy/proxy.ts` 301-redirects `/portal/{slug}/...` to
 *    `/{slug}/...`, so the form still surfaces the short URL to
 *    the user.
 *
 * The admin staff impersonation route at
 * `src/app/api/admin/impersonate/portal/route.ts` uses the same
 * form. Keep these two builders aligned.
 */
export function getPortalImpersonateUrl(token: string, orgSlug: string, customerId: string, nextPath?: string): string {
    const portalDashboard = buildPortalImpersonationNextPath(orgSlug, customerId, nextPath);
    const params = new URLSearchParams({ token, next: portalDashboard, slug: orgSlug });
    /** @calls GET /auth/portal/impersonate — the portal-impersonate action returns this url; usePortalActions opens it */
    return getPortalUrl(`/auth/portal/impersonate?${params.toString()}`);
}

type TenantPortalCacheEntry = { customDomain: string | null; slug: string | null; };

interface PortalTenantLookup {
    id: Id<"organizations">;
    slug: string;
}

async function getConvexHttpClientForTenantLookup() {
    const { getConvexHttpClient } = await import("@/lib/convex/server");
    return getConvexHttpClient();
}

// GOTCHA: This cache is in-memory per Vercel instance — cold starts clear it.
// The 1-minute TTL is intentionally shorter than the 5-minute tenant slug TTL
// (in src/lib/tenant.ts) because domain verification can change without a slug
// or ID change. A newly-verified custom domain would otherwise take 5 minutes
// to start routing to the custom URL. Do not increase this TTL.
const tenantDomainCache = new LRUCache<string, TenantPortalCacheEntry>({
    max: 500,
    ttl: MINUTE_MS, // 1 minute
});

/**
 * Get the portal URL for a tenant.
 * Returns custom domain if verified, otherwise default portal URL.
 *
 * @param tenantSlugOrId - Tenant slug or ID
 * @returns Full portal URL (custom domain or default)
 */
export async function getPortalUrlForTenant(tenantSlugOrId: string, path: string = "", overrides?: {
    resolveTenantBySlug?: (slug: string) => Promise<PortalTenantLookup | null>;
    resolveTenantById?: (id: Id<"organizations">) => Promise<PortalTenantLookup | null>;
    getVerifiedDomainForTenant?: (tenantId: Id<"organizations">) => Promise<string | null>;
    bypassCache?: boolean;
}): Promise<string> {
    const cacheKey = `tenant:${tenantSlugOrId}`;
    const normalizedPath = path ? (path.startsWith("/") ? path : `/${path}`) : "";

    // Check cache first (unless bypassed, e.g. test/downgrade-reactivation checks)
    if (!overrides?.bypassCache) {
        const cached = tenantDomainCache.get(cacheKey);
        if (cached !== undefined) {
            const baseUrl = cached.customDomain || getPortalUrl();
            if (cached.customDomain) {
                return `${baseUrl}${normalizedPath}`;
            }
            const fallbackSlug = cached.slug ?? tenantSlugOrId;
            const resolvedPath = normalizedPath ? (isDedicatedPortalHost(baseUrl) ? toShortPortalPath(normalizedPath) : normalizedPath) : isDedicatedPortalHost(baseUrl) ? `/${fallbackSlug}` : `/portal/${fallbackSlug}`;
            return normalizedPath ? `${baseUrl}${resolvedPath}` : `${baseUrl}${resolvedPath}`;
        }
    }

    try {
        // Resolve tenant from slug or ID first.
        const needsConvexClient = !overrides?.resolveTenantBySlug || !overrides?.resolveTenantById || !overrides?.getVerifiedDomainForTenant;
        const convex = needsConvexClient ? await getConvexHttpClientForTenantLookup() : null;

        let tenantId: Id<"organizations"> | null = null;
        let tenantSlug: string | null = null;

        const bySlug = overrides?.resolveTenantBySlug ? await overrides.resolveTenantBySlug(tenantSlugOrId.toLowerCase()) : await convex!.query(api.organizations.getTenantBySlug, { slug: tenantSlugOrId.toLowerCase() });
        if (bySlug) {
            tenantId = bySlug.id as Id<"organizations">;
            tenantSlug = bySlug.slug;
        } else {
            const byId = overrides?.resolveTenantById ? await overrides.resolveTenantById(tenantSlugOrId as Id<"organizations">) : await convex!.query(api.organizations.getTenantById, { id: tenantSlugOrId as Id<"organizations"> });
            if (byId) {
                tenantId = byId.id as Id<"organizations">;
                tenantSlug = byId.slug;
            }
        }

        if (!tenantId) {
            const baseUrl = getPortalUrl();
            const fallbackPath = normalizedPath ? (isDedicatedPortalHost(baseUrl) ? toShortPortalPath(normalizedPath) : normalizedPath) : isDedicatedPortalHost(baseUrl) ? `/${tenantSlugOrId}` : `/portal/${tenantSlugOrId}`;
            return normalizedPath ? `${baseUrl}${fallbackPath}` : `${baseUrl}${fallbackPath}`;
        }

        // Get verified domain for this tenant (public query)
        const verifiedDomain = overrides?.getVerifiedDomainForTenant ? await overrides.getVerifiedDomainForTenant(tenantId) : await convex!.query(api.organizations.getVerifiedDomainForTenant, { tenantId });

        const customDomain = verifiedDomain ? `https://${verifiedDomain}` : null;

        // Cache both custom domain and resolved slug.
        if (!overrides?.bypassCache) {
            tenantDomainCache.set(cacheKey, { customDomain, slug: tenantSlug });
        }

        const baseUrl = customDomain || getPortalUrl();
        if (customDomain) {
            return `${baseUrl}${normalizedPath}`;
        }

        const resolvedSlug = tenantSlug ?? tenantSlugOrId;
        const resolvedPath = normalizedPath ? (isDedicatedPortalHost(baseUrl) ? toShortPortalPath(normalizedPath) : normalizedPath) : isDedicatedPortalHost(baseUrl) ? `/${resolvedSlug}` : `/portal/${resolvedSlug}`;
        return normalizedPath ? `${baseUrl}${resolvedPath}` : `${baseUrl}${resolvedPath}`;
    } catch (error) {
        // On error, fall back to default portal URL
        logger("urls").error("[getPortalUrlForTenant] Error fetching custom domain:", error instanceof Error ? error : new Error(String(error)));
        const baseUrl = getPortalUrl();
        const fallbackPath = normalizedPath ? (isDedicatedPortalHost(baseUrl) ? toShortPortalPath(normalizedPath) : normalizedPath) : isDedicatedPortalHost(baseUrl) ? `/${tenantSlugOrId}` : `/portal/${tenantSlugOrId}`;
        return normalizedPath ? `${baseUrl}${fallbackPath}` : `${baseUrl}${fallbackPath}`;
    }
}

/**
 * Synchronous sibling of `getPortalUrlForTenant()` for component render paths
 * where the caller already has the tenant's slug + customDomain in hand and
 * cannot await an async query (e.g., settings UI URL previews).
 *
 * Resolution rules match `getPortalUrlForTenant()`:
 *   - If `customDomain` is set → `https://{customDomain}{path}`
 *   - Else on a dedicated portal host (portal.example.com) → `{portal-base}/{slug}{path}`
 *   - Else on shared host → `{portal-base}/portal/{slug}{path}`
 *
 * @param slug - Tenant slug (caller must already have this)
 * @param customDomain - Verified custom domain (no protocol, e.g. "acme.example.com") or null
 * @param path - Optional path to append (e.g., "/dashboard")
 * @returns Full URL string (synchronous — no Convex query, no DB lookup)
 *
 * @example
 *   buildPreviewPortalUrl("acme", null, "/dashboard")
 *   // → "https://acme.disputewell.com/dashboard" (production, dedicated portal host)
 *   // → "http://portal.lvh.me:3000/acme/dashboard" (local dev, dedicated portal host)
 *   // → "http://localhost:3000/portal/acme/dashboard" (local dev, shared host)
 *
 *   buildPreviewPortalUrl("acme", "acme.example.com", "/dashboard")
 *   // → "https://acme.example.com/dashboard"
 */
export function buildPreviewPortalUrl(slug: string, customDomain: string | null = null, path: string = ""): string {
    const normalizedPath = path ? (path.startsWith("/") ? path : `/${path}`) : "";

    // Custom domain wins
    if (customDomain && customDomain.trim()) {
        const trimmed = customDomain.trim();
        const customBase = trimmed.startsWith("http") ? normalizeBase(trimmed) : `https://${trimmed}`;
        return `${customBase}${normalizedPath}`;
    }

    // Default portal URL with appropriate slug prefix
    const baseUrl = getPortalUrl();
    const resolvedPath = normalizedPath ? (isDedicatedPortalHost(baseUrl) ? toShortPortalPath(normalizedPath) : normalizedPath) : isDedicatedPortalHost(baseUrl) ? `/${slug}` : `/portal/${slug}`;
    return `${baseUrl}${resolvedPath}`;
}

/**
 * Send the browser back to the admin host's impersonation page. Both
 * impersonation banners — the CRM one and the portal one — end their exit this
 * way, from whatever host the operator is currently impersonating on, so the
 * admin host is derived from the current one rather than from config.
 */
export function redirectToAdminImpersonation(): void {
    const rootDomain = window.location.hostname.split(".").slice(-2).join(".");
    const protocol = window.location.protocol;
    const port = window.location.port ? `:${window.location.port}` : "";
    window.location.href = `${protocol}//admin.${rootDomain}${port}/admin/impersonate`;
}

